/**
 * Управление содержимым <head> при навигации внутри SPA.
 *
 * Поисковому роботу мета-теги отдаёт сервер (nginx направляет краулеров на
 * /_seo/render, см. backend SeoRenderController) — здесь то же самое делается
 * для живого посетителя: при переходе между маршрутами меняются заголовок
 * вкладки, описание, canonical и Open Graph.
 *
 * Зачем это нужно и на клиенте:
 *  - вкладка и история браузера показывают заголовок страницы, а не один
 *    «DUSTAR.PRO» на весь сайт;
 *  - расширения и читалки, которые смотрят на живой DOM, видят актуальные теги;
 *  - Googlebot выполняет JS и читает уже обновлённый <head>.
 *
 * Теги помечаются data-seo, чтобы при уходе со страницы снимать ровно свои
 * и не трогать то, что стоит в index.html.
 */

import { onBeforeUnmount, unref, watchEffect } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

/** Канонический адрес сайта. Из него строятся абсолютные URL для og:url и canonical. */
export const SITE_URL = 'https://dustar.pro'
export const SITE_NAME = 'pole DUSTAR'
export const DEFAULT_DESCRIPTION =
  'Дневник и мастерская: код, музыка, тексты и опыты на стыке. Статьи, проекты и клипы pole DUSTAR.'
export const DEFAULT_IMAGE = '/og-default.webp'

export interface SeoMeta {
  /** Заголовок страницы без суффикса сайта — суффикс добавляется сам. */
  title?: string
  description?: string
  /** Путь от корня, например «/blog/moy-slug». По умолчанию — текущий адрес. */
  canonicalPath?: string
  image?: string
  type?: 'website' | 'article'
  noIndex?: boolean
  publishedAt?: string
  updatedAt?: string
  author?: string
  section?: string
  tags?: string[]
  /** Язык страницы: 'ru' | 'en' | 'zh'. Идёт в <html lang> и og:locale. */
  language?: string
  /**
   * Адреса этой же страницы на других языках: язык → путь от корня.
   * Из них строятся link rel="alternate" hreflang.
   */
  alternates?: Record<string, string>
  /** Готовые объекты JSON-LD. Сериализуются в <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown>[]
}

const LOCALES: Record<string, string> = {
  ru: 'ru_RU',
  en: 'en_US',
  zh: 'zh_CN',
}

const MANAGED_ATTR = 'data-seo'

function absolute(url: string | undefined, fallback: string): string {
  const value = url && url.trim() ? url : fallback
  if (/^https?:\/\//i.test(value)) return value
  return `${SITE_URL}/${value.replace(/^\//, '')}`
}

/** Создаёт или обновляет <meta>. Ключ — name или property, как в спецификации тега. */
function setMeta(kind: 'name' | 'property', key: string, content: string | undefined): void {
  if (!content) return

  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(kind, key)
    el.setAttribute(MANAGED_ATTR, '')
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    el.setAttribute(MANAGED_ATTR, '')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/** Снимает только теги, добавленные предыдущим вызовом: повторные переходы не плодят дубли. */
function clearManaged(selector: string): void {
  document.head
    .querySelectorAll(`${selector}[${MANAGED_ATTR}]`)
    .forEach((el) => el.remove())
}

function applyJsonLd(blocks: Record<string, unknown>[] | undefined): void {
  clearManaged('script[type="application/ld+json"]')

  if (!blocks?.length) return

  for (const block of blocks) {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute(MANAGED_ATTR, '')
    // Внутри <script> последовательность «</» закрывает блок досрочно —
    // текст статьи не должен иметь возможности разорвать разметку.
    script.textContent = JSON.stringify(block).replace(/<\//g, '<\\/')
    document.head.appendChild(script)
  }
}

/**
 * Проставляет мета-данные страницы. Принимает значение, ref или геттер —
 * при асинхронной загрузке (например, статьи) теги обновятся сами, как только
 * данные приедут.
 */
export function useSeo(meta: MaybeRefOrGetter<SeoMeta>): void {
  const stop = watchEffect(() => {
    const value = typeof meta === 'function' ? meta() : unref(meta)
    if (!value) return

    const title = value.title ? `${value.title} — ${SITE_NAME}` : SITE_NAME
    const description = value.description || DEFAULT_DESCRIPTION
    const path = value.canonicalPath || window.location.pathname
    const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
    const image = absolute(value.image, DEFAULT_IMAGE)
    const type = value.type || 'website'

    document.title = title

    setMeta('name', 'description', description)
    setLink('canonical', canonical)

    setMeta(
      'name',
      'robots',
      value.noIndex
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    )

    setMeta('property', 'og:type', type)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonical)
    setMeta('property', 'og:site_name', SITE_NAME)

    const language = value.language || 'ru'
    setMeta('property', 'og:locale', LOCALES[language] || 'ru_RU')

    // Атрибут lang должен соответствовать языку текста: страница на английском
    // под lang="ru" — прямой сигнал поисковику, что заявленный язык не тот.
    document.documentElement.setAttribute('lang', language)

    setMeta('property', 'og:image', image)
    setMeta('property', 'og:image:alt', title)

    // hreflang связывает языковые версии. Без него поисковик считает перевод
    // самостоятельной страницей: вес дробится, а читателю может выпасть не тот язык.
    clearManaged('link[rel="alternate"][hreflang]')
    const alternates = value.alternates
    if (alternates && Object.keys(alternates).length > 1) {
      for (const [lang, path] of Object.entries(alternates)) {
        const el = document.createElement('link')
        el.setAttribute('rel', 'alternate')
        el.setAttribute('hreflang', lang)
        el.setAttribute('href', `${SITE_URL}${path}`)
        el.setAttribute(MANAGED_ATTR, '')
        document.head.appendChild(el)
      }

      if (alternates.ru) {
        const el = document.createElement('link')
        el.setAttribute('rel', 'alternate')
        el.setAttribute('hreflang', 'x-default')
        el.setAttribute('href', `${SITE_URL}${alternates.ru}`)
        el.setAttribute(MANAGED_ATTR, '')
        document.head.appendChild(el)
      }
    }

    // Теги статьи снимаем перед установкой: при переходе с одной статьи на
    // другую иначе накапливались бы article:tag от обеих.
    clearManaged('meta[property^="article:"]')

    if (type === 'article') {
      setMeta('property', 'article:published_time', value.publishedAt)
      setMeta('property', 'article:modified_time', value.updatedAt)
      setMeta('property', 'article:author', value.author)
      setMeta('property', 'article:section', value.section)

      for (const tag of value.tags || []) {
        const el = document.createElement('meta')
        el.setAttribute('property', 'article:tag')
        el.setAttribute('content', tag)
        el.setAttribute(MANAGED_ATTR, '')
        document.head.appendChild(el)
      }
    }

    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image', image)

    applyJsonLd(value.jsonLd)
  })

  onBeforeUnmount(() => {
    stop()
    clearManaged('meta[property^="article:"]')
    clearManaged('script[type="application/ld+json"]')
    // hreflang остался бы от прошлой страницы и указывал не туда
    clearManaged('link[rel="alternate"][hreflang]')
  })
}

// ==================== Готовые блоки микроразметки ====================

export function breadcrumbJsonLd(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  }
}

export function articleJsonLd(input: {
  title: string
  description: string
  slug: string
  image?: string
  publishedAt?: string
  updatedAt?: string
  author?: string
  section?: string
  tags?: string[]
  readingMinutes?: number
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.title.slice(0, 110),
    description: input.description,
    image: [absolute(input.image, DEFAULT_IMAGE)],
    datePublished: input.publishedAt,
    dateModified: input.updatedAt || input.publishedAt,
    author: { '@type': 'Person', name: input.author || 'DUSTAR', url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}${DEFAULT_IMAGE}` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${input.slug}` },
    articleSection: input.section,
    keywords: input.tags?.length ? input.tags.join(', ') : undefined,
    timeRequired: input.readingMinutes ? `PT${Math.max(1, input.readingMinutes)}M` : undefined,
    inLanguage: 'ru-RU',
  }
}

/** ItemList для витрины: карточки внешних проектов, не статьи — своей страницы у них нет. */
export function itemListJsonLd(items: { name: string; url: string; image?: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: item.url,
      image: item.image ? absolute(item.image, DEFAULT_IMAGE) : undefined,
    })),
  }
}

export function webSiteJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    inLanguage: 'ru-RU',
  }
}
